import { useMemo, useRef } from 'react';

import { MESSAGES } from '../../constant/messages';
import { useProductOptionContext } from '../../context/ProductOptionProvider';
import { buildItemCard } from '../../core/item-card.core';
import { createAdditionalOptions } from '../../core/select-box-builder.core';
import { OptionData, ProductOptionConfig } from '../../type/common.type';
import { reportProductOptionError } from '../../util/executors/error-log-send-executor';
import { showZDSToastInOption } from '../../util/toast/zds-toast-in-option';
import { productMetadataBuilder } from '../../util/transformers/product-metadata-transformer';

const HEADER_HEIGHT = 20;

interface Props {
  config: ProductOptionConfig;
}

export const useAdditionalOptions = ({ config }: Props) => {
  const { state, actions } = useProductOptionContext();
  const product_metadata = productMetadataBuilder(state.option.info, config);
  const additional_ref = useRef<HTMLDivElement>(null);

  // 추가 상품 드롭다운 클릭
  const handleClickAdditionalSelectBoxDropdown = (disabled: boolean) => {
    if (disabled) {
      showZDSToastInOption(MESSAGES.SELECT_UPPER_OPTION);
      return;
    }
    actions.toggleAdditionalDropdown();
  };

  // 추가 상품 옵션 선택
  const handleAdditionalOptionSelect = (selected_option: OptionData) => {
    try {
      if (selected_option.stock_status?.is_sold_out) {
        return;
      }

      const selected_item = state.option.info?.catalog_product.additional_item_list?.find(
        (o) => o.id === selected_option.id,
      );

      if (!selected_item) {
        throw new Error(MESSAGES.PRODUCT_ITEM_NOT_FOUND);
      }

      actions.addItemCard(
        buildItemCard({
          item_cards: state.item_cards,
          selected_item: {
            ...selected_item,
            item_attribute_list: [{ ...selected_item.item_attribute_list[0], value: selected_option.name }],
          },
          context: {
            bogo_info: product_metadata.bogo_info,
            maxPurchaseQuantity: config?.maxPurchaseQuantity,
            min_order_quantity: product_metadata.min_order_quantity,
            is_order_quantity_product_type: product_metadata.is_order_quantity_product_type,
            is_order_quantity_item_type: product_metadata.is_order_quantity_item_type,
            is_store_deal_banner: product_metadata.has_store_deal_banner,
            enableSingleItemSelection: config?.enableSingleItemSelection,
          },
        }),
      );
    } catch (error) {
      reportProductOptionError({
        message: '추가 옵션 선택 완료 처리 중 오류가 발생했습니다.',
        originalError: error,
      });
    }
  };

  // 추가 상품 옵션 오픈 애니메이션 완료 후 실행되는 콜벅
  const handleAnimationComplete = (open: boolean) => {
    if (!additional_ref.current) {
      return;
    }

    try {
      const scroll_node = additional_ref.current.parentNode;
      const offset_y = additional_ref.current.offsetTop - HEADER_HEIGHT;

      if (scroll_node instanceof HTMLElement && open) {
        scroll_node.scrollTo({ top: offset_y, behavior: 'smooth' });
      }
    } catch (err) {
      reportProductOptionError({
        message: '추가 상품 옵션 스크롤 이동 중 오류가 발생했습니다.',
        originalError: err,
      });
    }
  };

  // 추가 상품 옵션 드롭다운 리스트
  const additional_option_dropdown_list = useMemo(() => {
    if (!state.option.info?.catalog_product.product_option_list || !state.option.map) {
      return [];
    }
    return createAdditionalOptions({
      option_info: state.option.info,
      is_item_card_mode_active: state.ui.is_item_card_mode_active,
      config,
      handleOptionSelect: handleAdditionalOptionSelect,
    });
  }, [state.option.info, state.ui.is_item_card_mode_active, config, handleAdditionalOptionSelect]);

  return {
    display: !config.hidden_flags?.additional_option_dropdown,
    display_header_text:
      !config.hidden_flags?.additional_option_header_text && additional_option_dropdown_list.length > 0,
    additional_option_dropdown_list,
    additional_dropdown_state: state.additional_dropdown_state,
    additional_ref,
    actions: {
      handleClickAdditionalSelectBoxDropdown,
      handleAnimationComplete,
    },
  };
};
